import React from 'react';
import { DollarSign, Trash2, Pencil, FileSpreadsheet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { formatDate } from '../../lib/dateUtils';

/**
 * Tabla de gastos con filtro por categoría + exportación Excel + acciones.
 */
export const GastosTable = ({
  gastos, filteredGastos,
  categorias,
  filtroCategoria, setFiltroCategoria,
  onExportExcel,
  onEditGasto,
  onDeleteGasto,
}) => (
  <Card>
    <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
      <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
        Historial de Gastos
      </CardTitle>
      <div className="flex gap-2 items-center">
        <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
          <SelectTrigger className="w-[200px] h-9">
            <SelectValue placeholder="Todas las categorias" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {categorias.map((c) => (
              <SelectItem key={c.id || c.nombre} value={c.nombre}>{c.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={onExportExcel}
          disabled={gastos.length === 0}
          data-testid="btn-export-gastos-excel"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      {filteredGastos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No hay gastos registrados</p>
          <p className="text-sm">Registre su primer gasto con el boton "Registrar Gasto"</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="tabla-gastos">
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Descripcion</TableHead>
                <TableHead className="text-right">Cant.</TableHead>
                <TableHead className="text-right">P/U</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="text-center">Tipo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGastos.map((g) => {
                const gastoId = g.id || g._id;
                return (
                  <TableRow key={gastoId} className="hover:bg-accent/60 transition-colors duration-150">
                    <TableCell>{formatDate(g.fecha)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{g.categoria}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm">{g.descripcion || '-'}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {g.cantidad ? `${g.cantidad} ${g.unidad || ''}` : '-'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {g.precio_unitario ? `$${g.precio_unitario}` : '-'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      ${g.monto?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-center">
                      {g.auto_generado ? (
                        <Badge variant="secondary" className="text-[10px]">Auto</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">Manual</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!g.auto_generado && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onEditGasto(g)}
                            title="Editar"
                            data-testid={`btn-edit-gasto-${gastoId}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDeleteGasto(g)}
                            title="Eliminar"
                            data-testid={`btn-delete-gasto-${gastoId}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);
